import { useEffect, useRef, useState } from 'react';

import type { LineClassificationDto, TaskDto } from '@time-tracking/shared';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

interface EditTaskDialogProps {
  task: TaskDto | null;
  classifications: LineClassificationDto[];
  onClose: () => void;
  onSubmit: (data: { name: string; classificationId: string; activeFlag: boolean }) => void;
  isPending: boolean;
}

export function EditTaskDialog({
  task,
  classifications,
  onClose,
  onSubmit,
  isPending,
}: EditTaskDialogProps) {
  const [name, setName] = useState('');
  const [classificationId, setClassificationId] = useState('');
  const [activeFlag, setActiveFlag] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(dialogRef, Boolean(task));

  useEffect(() => {
    if (!task) return;
    setName(task.name);
    setClassificationId(task.classificationId);
    setActiveFlag(task.activeFlag);
    setTimeout(() => firstInputRef.current?.focus(), 0);
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [task, onClose]);

  if (!task) return null;

  const canSubmit = name.trim() && classificationId && !isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), classificationId, activeFlag });
  }

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-task-dialog-title"
        className="projects-dialog"
      >
        <h2 id="edit-task-dialog-title" className="dialog-title">
          Edit task
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="dialog-form-field">
            <label htmlFor="task-name">
              Task name
              <span className="required-indicator" aria-hidden="true">
                {' '}
                *
              </span>
              <span className="sr-only"> (required)</span>
            </label>
            <input
              ref={firstInputRef}
              id="task-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-required="true"
              maxLength={255}
            />
          </div>

          <div className="dialog-form-field">
            <label htmlFor="task-classification">
              Type
              <span className="required-indicator" aria-hidden="true">
                {' '}
                *
              </span>
              <span className="sr-only"> (required)</span>
            </label>
            <select
              id="task-classification"
              value={classificationId}
              onChange={(e) => setClassificationId(e.target.value)}
              aria-required="true"
            >
              {classifications
                .filter((c) => c.activeFlag || c.classificationId === classificationId)
                .map((c) => (
                  <option key={c.classificationId} value={c.classificationId}>
                    {c.name}
                    {c.activeFlag ? '' : ' (inactive)'}
                  </option>
                ))}
            </select>
          </div>

          <div className="dialog-form-field dialog-form-field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={activeFlag}
                onChange={(e) => setActiveFlag(e.target.checked)}
              />
              <span>Active (visible on timesheets)</span>
            </label>
          </div>

          <div className="dialog-footer">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit}
              aria-busy={isPending}
            >
              {isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
